import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * A custom element that manages the site header with smart sticky behavior.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #offscreen = false;

  /** @type {number} */
  #lastScrollTop = 0;

  /** @type {number | null} */
  #scrollRafId = null;

  /**
   * Keeps --header-height CSS property up to date
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry?.borderBoxSize[0]) return;
    const height = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${height}px`);
  });

  /**
   * Observe sticky position via IntersectionObserver
   */
  #observeStickyPosition() {
    if (this.#intersectionObserver) return;

    const stickyMode = this.getAttribute('sticky');

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      if (stickyMode === 'always') {
        this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !entry.isIntersecting || this.dataset.stickyState === 'active';
      }
    }, { threshold: stickyMode === 'always' ? 1 : 0 });

    this.#intersectionObserver.observe(this);
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState() {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (stickyMode === 'always') {
      this.dataset.scrollDirection = isAtTop ? 'none' : (isScrollingUp ? 'up' : 'down');
      this.#lastScrollTop = scrollTop;
      return;
    }

    // scroll-up mode: show when scrolling up, hide when scrolling down
    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition();
      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      document.body.style.setProperty('--header-group-height', `${Math.round(headerGroupHeight)}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      if (children[i] instanceof HTMLElement) {
        resizeObserver.observe(children[i]);
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            if (children[i] instanceof HTMLElement) {
              resizeObserver.observe(children[i]);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
